import { useEffect } from "react";

import { MobileRoutingOverlay } from "components/MobileRoutingOverlay";
import { selectHomeContextForAi } from "components/ai/context/view-context";
import { useAIContext } from "components/ai/hooks/use-ai-context";
import { HomeFooter } from "components/footer/HomeFooter";
import { MainHeader } from "components/header/MainHeader";
import { MobileMainHeader } from "components/header/MobileMainHeader";
import { OrgSelect } from "components/materials/home/OrgSelect";
import { RoleSelect } from "components/materials/home/RoleSelect";
import { useHomeRole } from "components/materials/home/use-home-role";
import { PageSecondarySidebar } from "components/materials/page/PageSecondarySidebar";
import { t } from "i18n/i18n";
import { useIsLgOrLarger, useIsMdOrLarger } from "mds/hooks/use-responsive";
import { LoadingDotsIcon } from "mds/icons";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentCourseId,
  selectCurrentUserId,
  selectIsSidebarOpen,
  selectIsStudentPreview,
  selectUserById,
} from "store/selectors";
import { localStateActions } from "store/slices/local";
import { viewStateActions } from "store/slices/view";
import { usePromiseEffect } from "store/store-hooks";
import { trackUserProperty } from "utils/amplitude";
import { Outlet } from "utils/routing";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

export default function Layout() {
  const dispatch = useAppDispatch();
  const isLargeScreen = useIsLgOrLarger();
  const isMobile = !useIsMdOrLarger();
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const isSmallScreen = !useIsMdOrLarger();
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentUser = useAppSelector((s) => selectUserById(s, currentUserId));
  const userFirstName = currentUser?.first_name || currentUser?.last_name || "";
  const { currentRole } = useHomeRole();
  const isStudentPreview = useAppSelector(selectIsStudentPreview);
  const currentCourseId = useAppSelector(selectCurrentCourseId);

  // We use isLoading to guarantee child routes that courses/orgs and related information is loaded
  const { isLoading, error } = usePromiseEffect(async () => {
    return Promise.all([
      storeApi.courses.list(),
      storeApi.course_users.list(),
      storeApi.course_codes.list(),
      storeApi.terms.list(),
      storeApi.class_sessions.list(),
      storeApi.outcomes.list(),
    ]);
  }, []);

  useEffect(() => {
    if (currentCourseId) {
      dispatch(viewStateActions.setCurrentCourseId(null));
      trackUserProperty("courseId", null);
      trackUserProperty("courseRole", null);
    }
  }, [currentCourseId, dispatch]);

  useEffect(() => {
    if (currentRole) {
      dispatch(localStateActions.setLastHomeRole(currentRole));
    }
  }, [currentRole, dispatch]);

  useEffect(() => {
    if (isStudentPreview) {
      dispatch(viewStateActions.setIsStudentPreview(false));
    }
  }, [isStudentPreview, dispatch]);

  // TODO: Optimize or move so that re-renders here don't affect rest of the tree
  const homeAiContext = useAppSelector((state) => selectHomeContextForAi(state));
  useAIContext("home_view", homeAiContext, "system");

  return (
    <div className="flex h-full flex-col">
      {isMobile ? <MobileMainHeader /> : <MainHeader />}
      <div className="flex h-1 grow" data-sidebar-open={isMobile ? undefined : isSidebarOpen}>
        <div className="relative h-full max-w-full grow">
          <ErrorBoundary>
            <MobileRoutingOverlay />
            <div className="flex h-full w-full flex-row items-stretch overflow-hidden bg-white">
              <div className="flex max-h-full w-full flex-col items-center gap-6 overflow-hidden bg-white p-6 pb-0 md:flex-row md:items-start md:justify-center md:gap-10 lg:gap-16">
                <div className="flex h-full w-full max-w-[1200px] flex-col gap-6">
                  <div className="flex w-full flex-wrap justify-between gap-2">
                    <h1 className="h2">
                      {userFirstName && t("home.header.welcome", { firstName: userFirstName })}
                    </h1>
                    <div className="flex items-center gap-2">
                      <OrgSelect />
                      <RoleSelect isLoading={isLoading} />
                    </div>
                  </div>
                  <div className="h-full overflow-hidden">
                    {error ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <div className="text-red">Error: {error.message}</div>
                      </div>
                    ) : isLoading ? (
                      <div className="flex h-full w-full items-center justify-center">
                        <LoadingDotsIcon className="h-10 w-10" />
                      </div>
                    ) : (
                      <Outlet />
                    )}
                  </div>
                </div>
              </div>
              {!isSmallScreen && (
                <PageSecondarySidebar className="h-full min-w-[392px] max-w-[520px] flex-1 basis-1/3 overflow-y-auto px-3 py-4" />
              )}
            </div>
          </ErrorBoundary>
        </div>
      </div>
      {isLargeScreen && <HomeFooter />}
    </div>
  );
}
